import { ChangeDetectorRef, Component, EventEmitter, Output } from '@angular/core';
import { loadStripe, Stripe, StripeElements, StripePaymentElement } from '@stripe/stripe-js';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { environment } from '../../../../environments/environment';
import { CorporateService } from '../../../core/services/corporate.service';

/**
 * Captura de tarjeta para suscripciones en USD, vía Stripe Elements +
 * SetupIntent. Molde: PayzenPaymentComponent (mismo contrato de salida,
 * `paymentCompleted: EventEmitter<boolean>`, para que PlanComponent reaccione
 * igual sin importar qué pasarela se usó).
 *
 * Diferencia clave con Payzen: Stripe confirma el SetupIntent del lado del
 * cliente (`stripe.confirmSetup`), pero el backend se entera de que el método
 * de pago quedó listo por WEBHOOK (`setup_intent.succeeded`), que puede
 * tardar un poco en llegar. Por eso, tras confirmar, se hace un poll corto de
 * `GET /corporate/payment-method` antes de avisar que terminó — si se
 * avisara de inmediato, `createStripeSubscription` (que lee el método de
 * pago desde la transacción que actualiza el webhook) podría fallar por una
 * carrera con el propio webhook.
 */
@Component({
  selector: 'app-stripe-payment',
  standalone: false,
  templateUrl: './stripe-payment.component.html',
  styleUrl: './stripe-payment.component.scss',
})
export class StripePaymentComponent {
  visible = false;
  loading = false;
  submitting = false;
  errorMessage = '';

  @Output() paymentCompleted = new EventEmitter<boolean>();

  private stripe: Stripe | null = null;
  private elements: StripeElements | null = null;
  private paymentElement: StripePaymentElement | null = null;

  private static readonly PAYMENT_METHOD_POLL_ATTEMPTS = 6;
  private static readonly PAYMENT_METHOD_POLL_DELAY_MS = 1000;

  constructor(
    private corporateService: CorporateService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private transloco: TranslocoService,
  ) {}

  async showStripeForm(clientSecret: string): Promise<void> {
    this.visible = true;
    this.loading = true;
    this.errorMessage = '';
    this.cdr.detectChanges();

    try {
      this.stripe = await loadStripe(environment.stripePublishableKey);
      if (!this.stripe) {
        throw new Error(this.transloco.translate('payment.stripeInitFailed'));
      }

      this.elements = this.stripe.elements({ clientSecret });
      this.paymentElement = this.elements.create('payment');

      // Mismo patrón que Payzen (setTimeout + getElementById): el contenedor
      // del dialog aún no existe en el DOM en el mismo ciclo en que
      // `visible` pasa a true.
      setTimeout(() => {
        const container = document.getElementById('stripe-payment-element-container');
        if (!container || !this.paymentElement) return;
        this.paymentElement.mount('#stripe-payment-element-container');
        this.paymentElement.on('ready', () => {
          this.loading = false;
          this.cdr.detectChanges();
        });
      }, 0);
    } catch (error) {
      console.error('Error inicializando Stripe:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.transloco.translate('common.error'),
        detail: this.transloco.translate('payment.paymentFormLoadFailed'),
      });
      this.loading = false;
      this.visible = false;
      this.cdr.detectChanges();
    }
  }

  async submitCard(): Promise<void> {
    if (!this.stripe || !this.elements || this.submitting) return;

    this.submitting = true;
    this.errorMessage = '';
    this.cdr.detectChanges();

    const { error } = await this.stripe.confirmSetup({
      elements: this.elements,
      redirect: 'if_required',
    });

    if (error) {
      this.errorMessage = error.message || this.transloco.translate('payment.cardProcessFailed');
      this.submitting = false;
      this.cdr.detectChanges();
      return;
    }

    const ready = await this.waitForPaymentMethodReady();
    this.submitting = false;

    if (!ready) {
      this.errorMessage = this.transloco.translate('payment.cardConfirmedSlow');
      this.cdr.detectChanges();
      return;
    }

    this.messageService.add({
      severity: 'success',
      summary: this.transloco.translate('payment.cardAccepted'),
      detail: this.transloco.translate('payment.paymentMethodRegistered'),
    });
    this.visible = false;
    this.cdr.detectChanges();
    this.paymentCompleted.emit(true);
  }

  /**
   * El SetupIntent ya se confirmó en Stripe, pero el backend recién se entera
   * por webhook — sin este poll, un create-subscription inmediatamente
   * después podría no encontrar el payment method todavía.
   */
  private async waitForPaymentMethodReady(): Promise<boolean> {
    for (let attempt = 0; attempt < StripePaymentComponent.PAYMENT_METHOD_POLL_ATTEMPTS; attempt++) {
      try {
        const status = await firstValueFrom(this.corporateService.getStripePaymentMethodStatus());
        if (status.hasPaymentMethod) return true;
      } catch (err) {
        console.error('Error consultando estado del método de pago:', err);
      }
      await new Promise(resolve => setTimeout(resolve, StripePaymentComponent.PAYMENT_METHOD_POLL_DELAY_MS));
    }
    return false;
  }

  onDialogHide(): void {
    this.paymentElement?.unmount();
    this.paymentElement = null;
    this.elements = null;
    this.loading = false;
    this.submitting = false;
    this.errorMessage = '';
  }
}
